/** VistoeraTASK-00342 — per-slot ready-to-accept highlight while a
 * compatible Canvas connection is dragged (React Flow synthetic pointer
 * gesture). ONE window `pointermove` listener per active connection;
 * rAF-batched; clears on leave / drop / cancel / unmount. */

import { useConnection } from '@xyflow/react'
import { type RefObject, useEffect } from 'react'
import { nodeOutputDataTypes } from './dataflow'
import type { FlowNodeData } from './types'
import { isGenerateVideoDropTargetCompatible } from './generateVideoSlotDropInteraction'
import { resolveGenerateVideoDropTarget, VIDEO_INPUT_PORT_ATTR } from './videoInputPorts'

export const VIDEO_SLOT_DROP_TARGET_ACTIVE_CLASS = 'is-drop-target-active'

export function useGenerateVideoSlotDropHighlight(shellRef: RefObject<HTMLDivElement | null>): void {
  const connection = useConnection()
  const inProgress = connection.inProgress
  const fromNodeType = inProgress
    ? ((connection.fromNode?.data as unknown as FlowNodeData | undefined)?.nodeType ?? null)
    : null

  useEffect(() => {
    if (!inProgress || !fromNodeType) return
    const shell = shellRef.current
    if (!shell) return
    // Sanity: source must declare at least one output type (same guard
    // `validateDropZoneTarget` ultimately uses via `getNodeIO`).
    if (nodeOutputDataTypes(fromNodeType).length === 0) return

    let raf = 0
    let activeEl: Element | null = null
    const clear = () => {
      if (activeEl) {
        activeEl.classList.remove(VIDEO_SLOT_DROP_TARGET_ACTIVE_CLASS)
        activeEl = null
      }
    }
    const onMove = (e: PointerEvent) => {
      cancelAnimationFrame(raf)
      raf = requestAnimationFrame(() => {
        const el = document.elementFromPoint(e.clientX, e.clientY)
        const resolved = el ? resolveGenerateVideoDropTarget(el) : null
        const compatible =
          resolved?.targetHandle != null &&
          isGenerateVideoDropTargetCompatible(fromNodeType, resolved.targetHandle)
        const zoneEl = compatible && el ? el.closest(`[${VIDEO_INPUT_PORT_ATTR}]`) : null
        const nextEl = zoneEl && shell.contains(zoneEl) ? zoneEl : null
        if (nextEl === activeEl) return
        clear()
        if (nextEl) {
          nextEl.classList.add(VIDEO_SLOT_DROP_TARGET_ACTIVE_CLASS)
          activeEl = nextEl
        }
      })
    }
    window.addEventListener('pointermove', onMove)
    return () => {
      window.removeEventListener('pointermove', onMove)
      cancelAnimationFrame(raf)
      clear()
    }
  }, [fromNodeType, inProgress, shellRef])
}
